import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ScrollableCurriculumTabStrip,
  type ScrollableCurriculumTabStripHandle,
} from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import {
  CurriculumParentStripNavSyncContext,
  useCurriculumParentStripNavSync,
} from "@/contexts/CurriculumParentStripNavSyncContext";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { BeltRank } from "../../MembersArea";

const JOHWA_TAB_LABELS = [
  "Il Jang",
  "Ee Jang",
  "Sam Jang",
  "Sa Jang",
  "Oh Jang",
  "Yuk Jang",
  "Chil Jang",
] as const;
const JOHWA_DEMO_ALIASES = ["johwa", "jo_hwa"];
const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

const joonbi =
  "Make a diamond with hands in low front, rise-up directly (pause at top), continue to circle arms out and round, on downward circle as hands reach shoulders slowly turn right hand into a fist and as come together at bottom, step left foot in and left hand covers right fist in a C.";

const beginnerMoves = [
  "Step out right foot into horse stance, deliver Innae 1.",
  "Turn to left, back stance and down block, adjust to front stance and reverse mid punch, rear leg front kick placing foot forward, lead hand chop reverse punch (mid), step up (feet together) then step forward (right foot) into front stance and deliver at same time left down parry and mid punch (leave parry hand out).\n\nTurn 180 (back-end round and repeat). Back stance and down block, adjust to front stance and reverse mid punch, adjust to front stance and reverse mid punch, rear leg front kick placing foot forward, lead hand chop reverse punch (mid), step up (feet together) then step forward (left foot) into front stance and deliver at same time right down parry and mid punch (leave parry hand out).",
  "Right leg back into back stance with rising block, adjust into front stance and high punch, rear sidekick landing forward in front stance with high chop reverse punch. Step up then forward into front stance with left hand small circle knife hand block and right high punch (both delivered at same time and leave blocking hand out).\n\nTurn 180 (Back-end round and repeat). Left leg back into back stance with rising block, adjust into front stance and high punch, rear sidekick landing forward in front stance with high chop, reverse punch. Step up then forward into front stance with right hand small circle knife hand block and left high punch (both delivered at same time and leave blocking hand out).",
  "Right leg back into back stance with inner forearm outer block, double mid punch, draw (snap) left leg crane (sidekick chamber), sidekick land in front stance (left lead), chop, mid punch, rear leg roundhouse (skip) one-step jump roundhouse, land front stance with right lead hand pressing cover block and kempo fist over.\n\nTurn 180 (Back-end round and repeat). Left leg back into back stance with inner forearm outer block, double mid punch, draw (snap) right leg crane (sidekick chamber), sidekick land in front stance (right lead), chop, mid punch, rear leg roundhouse (skip) one-step jump roundhouse, land front stance with left lead hand pressing cover block and kempo fist over.",
];

const advancedMoves = [
  ...beginnerMoves,
  "Right leg back into back stance, left knife hand block, adjust into front stance and deliver reverse knife hand strike to neck, pull back (casual / cat stance) with rear arm high covering side of face and lead arm lower covering body. Lead leg roundhouse, back kick, land front stance with lead hand parry and reverse punch, adjust over to left (open side), 360° spin sidekick, land tiger stance, lead hand long uppercut with rear hand downward parry.\n\nStep through turn 180 and repeat. Left leg back into back stance, right knife hand block, adjust into front stance and deliver reverse knife hand strike to neck, pull back (casual / cat stance) with rear arm high covering side of face and lead arm lower covering body. Lead leg roundhouse, back kick, land front stance with lead hand parry and reverse punch, adjust over to left (open side), 360° spin sidekick, land tiger stance, lead hand long uppercut with rear hand downward parry.",
  "Right leg back into back stance with knife hand guarding block, slide back, lead leg sidekick landing in back stance (same leg lead) with crossing knife hand guarding block, double bounce, rear leg wheel kick, back kick, skip axe (draw step), land front stance with left (lead) hand parry down and right reverse punch, stay in place and right parry down with left punch mid, skip change landing in back stance with crossing knife hand guarding block.\n\nTurn 180 back-end round and repeat. Left leg back into back stance knife hand guarding block, slide back, lead leg sidekick landing in back stance (same leg lead) with crossing knife hand guarding block, double bounce, rear leg wheel kick, back kick, skip axe (draw step), land front stance with right lead hand parry down and left reverse punch, stay in place and left parry down with right punch mid, skip change landing in back stance with crossing knife hand guarding block.",
  "Right leg spin 270° to back into back stance with inner lead left hammer block, rear hand mid punch, skip change into back stance with crossing knife hand block, rear leg round kick adjust over to right (open) side, rear round kick, tornado kick, back kick, land front stance with reverse punch and lead hand parry down.\n\nStep through into mantis (face the rear) with rear knife hand to shoulder and lead arm knife hand down block, jump skip change into back stance with lead right inner hammer block, rear hand mid punch, skip change into back stance with crossing knife hand block, rear leg round kick land adjust to left (open) side, rear round kick, tornado kick, back kick land front stance with reverse punch and lead hand parry down.\n\nStep through turn 180 into mantis with rear knife hand to shoulder and lead arm knife hand down block. At end turn body away (head still forward) drop right knee down with eagle/crane arms/hands, then step up into Habeul Jah Seh (joonbi).",
];

interface JoHwaProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
}

interface JoHwaPattern {
  number: number;
  name: string;
  meaning: string;
  movements: string;
  keyTechniques: string[];
}

const joHwaPatterns: JoHwaPattern[] = [
  {
    number: 1,
    name: "JoHwa Il Jang",
    meaning: "Harmony First Form",
    movements:
      "A sequence of 36 movements focusing on foundational harmony techniques",
    keyTechniques: [
      "Basic harmony stances",
      "Foundational flowing movements",
      "Initial circular techniques",
      "Basic energy redirection",
      "Fundamental balance points",
    ],
  },
  {
    number: 2,
    name: "JoHwa Ee Jang",
    meaning: "Harmony Second Form",
    movements: "42 movements building upon the first form's harmonious principles",
    keyTechniques: [
      "Intermediate harmony stances",
      "Advanced flowing movements",
      "Complex circular techniques",
      "Energy redirection combinations",
      "Advanced balance transitions",
    ],
  },
  {
    number: 3,
    name: "JoHwa Sam Jang",
    meaning: "Harmony Third Form",
    movements: "48 movements demonstrating advanced harmony concepts",
    keyTechniques: [
      "Advanced harmony stances",
      "Master-level flowing movements",
      "Expert circular techniques",
      "Multiple energy redirections",
      "Complex balance sequences",
    ],
  },
  {
    number: 4,
    name: "JoHwa Sa Jang",
    meaning: "Harmony Fourth Form",
    movements: "54 movements showcasing complex harmony applications",
    keyTechniques: [
      "Expert harmony stances",
      "Supreme flowing movements",
      "Master circular techniques",
      "Advanced energy manipulation",
      "Expert balance mastery",
    ],
  },
  {
    number: 5,
    name: "JoHwa Oh Jang",
    meaning: "Harmony Fifth Form",
    movements: "60 movements incorporating advanced harmony principles",
    keyTechniques: [
      "Master-level harmony stances",
      "Perfect flowing movements",
      "Supreme circular techniques",
      "Complete energy mastery",
      "Perfect balance control",
    ],
  },
  {
    number: 6,
    name: "JoHwa Yuk Jang",
    meaning: "Harmony Sixth Form",
    movements: "66 movements demonstrating supreme harmony mastery",
    keyTechniques: [
      "Supreme harmony stances",
      "Ultimate flowing movements",
      "Perfect circular techniques",
      "Supreme energy control",
      "Ultimate balance mastery",
    ],
  },
  {
    number: 7,
    name: "JoHwa Chil Jang",
    meaning: "Harmony Seventh Form",
    movements: "72 movements representing complete harmony mastery",
    keyTechniques: [
      "Complete harmony mastery",
      "Perfect movement flow",
      "Ultimate circular mastery",
      "Supreme energy harmony",
      "Perfect balance unity",
    ],
  },
];

function JoHwaTheoryPanels({ pattern }: { pattern: JoHwaPattern }) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
        <p className="mb-4 text-gray-700">
          {pattern.name} is an advanced MooGongRyu pattern that represents the principles of harmony in
          martial arts.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
        <p className="text-gray-700">{pattern.meaning}</p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
        <p className="text-gray-700">{pattern.movements}</p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          {pattern.keyTechniques.map((technique, index) => (
            <li key={index}>{technique}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function JoHwaMovesBody({ patternIndex }: { patternIndex: number }) {
  const moves =
    patternIndex < 4 ? beginnerMoves[patternIndex] : advancedMoves[patternIndex];
  const blocks = moves.split("\n\n");

  return (
    <div className="space-y-4 text-gray-700">
      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">Joonbi</h4>
        <p>{joonbi}</p>
      </div>
      {blocks.map((block, i) => (
        <p key={i} className="whitespace-pre-wrap">
          {block}
        </p>
      ))}
    </div>
  );
}

export default function JoHwa({
  selectedBelt,
  getBeltColor: _getBeltColor,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
  hasDemo,
  onWatchDemo,
}: JoHwaProps) {
  const getAvailablePatterns = () => {
    switch (selectedBelt) {
      case "BLACK/D1":
        return joHwaPatterns.slice(0, 4);
      case "BLACK/D2":
      case "BLACK/D3":
      case "BLACK/D4":
      case "BLACK/D5":
        return joHwaPatterns.slice(0, 7);
      default:
        return [];
    }
  };

  const availablePatterns = getAvailablePatterns();
  if (availablePatterns.length === 0) return null;

  const fingerprint = availablePatterns.map((p) => p.number).join("|");
  const [activeTab, setActiveTab] = useState(() => String(availablePatterns[0]?.number ?? 1));

  useEffect(() => {
    setActiveTab(String(availablePatterns[0]?.number ?? 1));
  }, [fingerprint]);

  const surface = beltTabSurfaceClass;
  const upstreamParentStripSync = useCurriculumParentStripNavSync();
  const joFormsStripRef = useRef<ScrollableCurriculumTabStripHandle>(null);
  const scrollJoFormsStrip = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      joFormsStripRef.current?.getScrollContainer() ?? null,
    );
  }, []);
  const scrollJoInnerStripAncestors = useCallback(() => {
    scrollJoFormsStrip();
    upstreamParentStripSync?.();
  }, [scrollJoFormsStrip, upstreamParentStripSync]);

  if (presentation === "curriculum") {
    return (
      <div className="space-y-4 text-left">
        {hasDemo?.(JOHWA_DEMO_ALIASES) && (
          <div>
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => onWatchDemo?.(JOHWA_DEMO_ALIASES)}
            >
              Watch Demonstration Video
            </button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ref={joFormsStripRef} key={fingerprint} ariaLabelPrefix="JoHwa forms">
            {availablePatterns.map((p) => (
              <TabsTrigger
                key={p.number}
                value={String(p.number)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium transition-shadow",
                  surface,
                  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                )}
              >
                {JOHWA_TAB_LABELS[p.number - 1] ?? p.name}
              </TabsTrigger>
            ))}
          </ScrollableCurriculumTabStrip>

          <CurriculumParentStripNavSyncContext.Provider value={scrollJoInnerStripAncestors}>
          {availablePatterns.map((p) => (
            <TabsContent
              key={p.number}
              value={String(p.number)}
              className="mt-0 focus-visible:outline-none"
            >
              <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
                <Tabs key={p.number} defaultValue="theory" className="w-full space-y-4">
                  <ScrollableCurriculumTabStrip ariaLabelPrefix={`JoHwa ${p.name}`}>
                    <TabsTrigger
                      value="theory"
                      className={cn(
                        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                        surface,
                        "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                      )}
                    >
                      Theory
                    </TabsTrigger>
                    <TabsTrigger
                      value="moves"
                      className={cn(
                        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                        surface,
                        "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                      )}
                    >
                      Moves
                    </TabsTrigger>
                  </ScrollableCurriculumTabStrip>
                  <TabsContent value="theory" className="mt-0 focus-visible:outline-none">
                    <JoHwaTheoryPanels pattern={p} />
                  </TabsContent>
                  <TabsContent value="moves" className="mt-0 focus-visible:outline-none">
                    <JoHwaMovesBody patternIndex={p.number - 1} />
                  </TabsContent>
                </Tabs>
              </div>
            </TabsContent>
          ))}
          </CurriculumParentStripNavSyncContext.Provider>
        </Tabs>
      </div>
    );
  }

  return (
    <Accordion type="single" collapsible className="w-full space-y-2">
      {availablePatterns.map((pattern) => (
        <AccordionItem key={`johwa-${pattern.number}`} value={`johwa-${pattern.number}`}>
          <AccordionTrigger className="rounded-lg bg-gray-50 px-4 py-2 hover:bg-gray-100 hover:no-underline">
            <div className="flex w-full items-center text-left">
              <span className="flex-1">{pattern.name}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0">
            <div className="mt-2 rounded-lg bg-gray-50 p-4">
              <JoHwaTheoryPanels pattern={pattern} />
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
